import { createContext, use, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router";
import { APIGetAppointments, APIGetChats, APIGetChatSettings, APIGetChatStats, APIGetDashboardStats, APIGetEducationalMaterials, APIGetPatients, APIGetTests, APIGetUnreadCounts, APIVerifyToken, createNotificationWebSocketConnection, APIGetClientRequests } from "./api";
import { Typography } from "@mui/joy";
import {
  getAccessToken, getRefreshToken, setTokens, clearTokens, installAuthInterceptor, apiLogout, apiGetMe,
} from "./auth";
import { autoSubscribeIfNeeded, setupPushMessageListener, notifySWVisibility, updateAppBadge } from './push';


export const LayoutContext = createContext();


// Функция для проверки и парсинга Telegram WebApp данных
const getTelegramWebAppData = () => {
  if (window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp;
    
    // Инициализируем WebApp
    tg.ready();
    tg.expand();

    if (window.innerWidth <= 768) {
        tg.requestFullscreen();
    }
    
    // Получаем данные пользователя
    const initData = tg.initData || '';
    const user = tg.initDataUnsafe?.user;
    
    if (user) {
      return {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        language_code: user.language_code,
        is_premium: user.is_premium || false,
        initData: initData
      };
    }
  }
  return null;
};

// Функция для создания заголовков с Telegram данными
const getTelegramAuthHeaders = (telegramData) => {
  if (!telegramData) return {};
  
  return {
    'X-Telegram-Init-Data': telegramData.initData || '',
    'X-Telegram-User-ID': telegramData.id.toString()
  };
};

export default function LayoutProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [ token, setToken ] = useState(null);
    const [ isVerified, setIsVerified ] = useState(false);
    const [ isVerifying, setIsVerifying ] = useState(true);
    const [ currentDoctor, setCurrentDoctor ] = useState(null);
    const [ account, setAccount ] = useState(null);
    
    const [patients, setPatients] = useState([]);
    const [chats, setChats] = useState([]);
    const [tests, setTests] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState({
        patients: false,
        chats: false,
        tests: false,
        materials: false
    });

    const [isPatient, setIsPatient] = useState(false);
    const [ registrationStatus, setRegistrationStatus ] = useState('approved');

    const [ selectedChatToken, setSelectedChatToken ] = useState(null);
    // Хранение количества заявок
    const [ newRequestsCount, setNewRequestsCount ] = useState(0);

    const [modalUser, setModalUser] = useState({
        open: false,
        userId: null,
        onLoadData: null,
    });

    const [modalResults, setModalResults] = useState({
        open: false,
        resultId: null
    });

    const [modalAssignTest, setModalAssignTest] = useState(false);

    const [unreadCounts, setUnreadCounts] = useState({
        unread_counts: [],
        timestamp: 0
    });

    const [dashboardStats, setDashboardStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Статистика чатов (отдельный эндпоинт /api/main/chat-stats/)
    const [chatStats, setChatStats] = useState(null);
    const [chatStatsLoading, setChatStatsLoading] = useState(false);

    // Клиентские настройки чата (лимит символов, размер файла и т.п.).
    // Подтягиваются один раз с /api/main/chat-settings/.
    // Дефолтные значения совпадают с серверными — фронт не сломается,
    // если эндпоинт временно недоступен или ещё не выкачен.
    const [chatSettings, setChatSettings] = useState({
        patient_message_max_length: 4000,
        message_edit_window_minutes: 15,
        chat_max_file_size_mb: 50,
        doctor_response_deadline_hours: 24,
    });
    const [chatSettingsLoading, setChatSettingsLoading] = useState(false);

    // Количество просроченных приёмов (для бейджа в Sidebar).
    // Обновляется автоматически каждые 30 минут.
    const [overdueAppointmentsCount, setOverdueAppointmentsCount] = useState(0);

    useEffect(() => {
        const _tg = window?.Telegram?.WebApp;
        try{
            if (_tg){
                // Сообщаем Telegram, что WebApp готово
                _tg?.ready();
                // Разворачиваем WebApp на весь экран
                _tg?.expand();
                // отключить вертикальный свайп
                _tg?.disableVerticalSwipes();
                // Настраиваем цвет верхней шапки ТГ
                _tg?.setHeaderColor('#2178DD');
            }
        }
        catch {}
    }, []);

    // Проверка токена при монтировании
    useEffect(() => {
        // Прозрачный refresh по 401 + выход на логин, если refresh не удался
        installAuthInterceptor({
            onRefreshed: (newAccess) => setToken(newAccess),
            onAuthFail: () => {
                clearTokens();
                setIsVerified(false);
                // Куда возвращать — зависит от текущего раздела, а не от query-параметра (баг #3):
                // кабинет/логин/регистрация пациента → форма пациента; иначе → форма врача.
                const path = window.location.pathname || '';
                const isPatientArea =
                    path.startsWith('/cabinet') ||
                    path.startsWith('/login/patient') ||
                    path.startsWith('/register/patient');
                navigate(isPatientArea ? '/login/patient' : '/login/doctor', { replace: true });
            },
        });

        const verifyToken = async () => {
            try {
                const params = new URLSearchParams(location.search);
                const urlToken = params.get('token');
                const urlAccess = params.get('access');
                const urlRefresh = params.get('refresh');
                const patientType = Boolean(params.get('patient_type') == null ? false : params.get('patient_type'));
                const chatId = params.get('chatId');

                setIsPatient(patientType);

                // access/refresh могли прийти в URL — сохраняем
                if (urlAccess) setTokens({ access: urlAccess, refresh: urlRefresh });

                // 1) Telegram WebApp (легаси)
                const tgData = getTelegramWebAppData();
                let authToken;
                if (tgData) {
                    authToken = tgData?.id;
                } else {
                    // 2) новый access (куки) либо токен из URL/куки (легаси)
                    authToken = getAccessToken() || urlToken || Cookies.get('authToken');
                }

                if (!authToken) {
                    throw new Error('Токен не найден');
                }

                const response = await APIVerifyToken(authToken);

                setToken(authToken);
                setCurrentDoctor(response);
                setAccount(response);
                setRegistrationStatus(response.registration_status || 'approved');
                setIsVerified(true);
                setNewRequestsCount(response.new_requests_count || 0);

                // дублируем токен в authToken для совместимости со старым кодом
                Cookies.set('authToken', getAccessToken() || authToken);

                if (urlToken || urlAccess) {
                    navigate(location.pathname, { replace: true });
                }

                // Маршрутизация по роли
                const roles = response.type_profile || [];
                const isStaff = ['admin', 'doctor', 'duty_doctor', 'moderator', 'supervisor'].some(r => roles.includes(r));
                if (!isStaff) {
                    setIsPatient(true);
                    if (location.pathname === '/' || location.pathname === '') {
                        navigate('/cabinet', { replace: true });
                    }
                } else if (patientType && chatId == null) {
                    navigate('/chats', { replace: true });
                }
            } catch (error) {
                console.error('Ошибка верификации:', error);
                setIsVerified(false);
            } finally {
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, []);

    
    // Загрузка данных при изменении токена
    useEffect(() => {
        if (token && isVerified) {
            loadInitialData();
        }
    }, [token, isVerified]);

    const realtimeTokenRef = useRef(token);
    realtimeTokenRef.current = token;
    const realtimeAccountRef = useRef(account?.id);
    realtimeAccountRef.current = account?.id;
    const realtimeCallbacksRef = useRef({});
    const unreadFlightRef = useRef(null);
    const unreadQueuedRef = useRef(false);
    const currentRealtimeToken = () => getAccessToken() || realtimeTokenRef.current;

    useEffect(() => {
        if (!token || !isVerified || !account?.id) return;
        const owner = account.id;
        let disposed = false;
        let refreshTimer;
        const refresh = () => {
            clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => {
                if (disposed || realtimeAccountRef.current !== owner) return;
                realtimeCallbacksRef.current.loadsUnreadCounts?.();
                realtimeCallbacksRef.current.loadChats?.().catch(() => {});
            }, 200);
        };
        const conn = createNotificationWebSocketConnection(currentRealtimeToken, (packet) => {
            if (disposed) return;
            if (packet.type === 'initial_data') {
                setUnreadCounts({ unread_counts: packet.unread_counts || [], timestamp: Date.now() });
            }
            if (['initial_data', 'new_message', 'unread_update', 'new_chat'].includes(packet.type)) refresh();
        }, (connected) => { if (connected) refresh(); }, {
            onAuthExpired: () => apiGetMe(), onWake: refresh,
        });
        const poll = setInterval(() => {
            if (document.visibilityState === 'visible') refresh();
        }, 60000);
        return () => { disposed = true; clearTimeout(refreshTimer); clearInterval(poll); conn.close(); };
    }, [token, isVerified, account?.id]);

    // Бейдж на иконке приложения = суммарное число непрочитанных.
    // unreadCounts обновляется из WS/пуша (loadsUnreadCounts), поэтому бейдж всегда актуален.
    useEffect(() => {
        const total = (unreadCounts.unread_counts || []).reduce((s, u) => s + (u.unread_count || 0), 0);
        updateAppBadge(total);
    }, [unreadCounts]);

    // Загрузка всех начальных данных
    const loadInitialData = async () => {
        await Promise.all([
            loadPatients(),
            loadChats(),
            loadChatSettings(),
            // loadTests(),
            // loadMaterials()
        ]);
    };

    // Push: авто-подписка + обработка кликов/получения уведомлений
    useEffect(() => {
        if (!token || !isVerified) return;

        autoSubscribeIfNeeded();

        const cleanup = setupPushMessageListener({
            onNavigate: (url) => {
                let target = url || '/';
                const roles = account?.type_profile || [];
                const isStaff = ['admin', 'doctor', 'duty_doctor', 'moderator', 'supervisor'].some(r => roles.includes(r));
                // пациент не ходит в разделы персонала, персонал — не в кабинет пациента
                if (!isStaff && (target.startsWith('/chats') || target.startsWith('/client-requests'))) {
                    target = '/cabinet';
                }
                if (isStaff && target.startsWith('/cabinet')) target = '/chats';
                navigate(target);
            },
            onPush: () => { try { loadsUnreadCounts?.(); } catch {} },
        });

        const onVis = () => notifySWVisibility(document.visibilityState === 'visible');
        onVis();
        document.addEventListener('visibilitychange', onVis);

        return () => { cleanup?.(); document.removeEventListener('visibilitychange', onVis); };
    }, [token, isVerified, account]);

    // Загрузка пациентов
    const loadPatients = async (params = {}) => {
        try {
            setLoading(prev => ({ ...prev, patients: true }));

            // Если пользователь врач и не админ, загружаем только его пациентов
            if (currentDoctor?.type_profile?.includes('doctor') && !currentDoctor?.type_profile?.includes('admin') && !currentDoctor?.type_profile?.includes('supervisor')) {
                params.doctor_id = currentDoctor.id;
            }

            const data = await APIGetPatients(token, params);
            setPatients(data.profiles || []);
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке пациентов:", error);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, patients: false }));
        }
    };

    // Загрузка чатов
    const chatLoadSeqRef = useRef(0);
    const loadChats = async (params = {}) => {
        const owner = realtimeAccountRef.current;
        const sequence = ++chatLoadSeqRef.current;
        try {
            setLoading(prev => ({ ...prev, chats: true }));
            const data = await APIGetChats(currentRealtimeToken(), params);
            if (realtimeAccountRef.current === owner && sequence === chatLoadSeqRef.current)
                setChats(data.chats || []);
            return data;
        } finally {
            if (sequence === chatLoadSeqRef.current) setLoading(prev => ({ ...prev, chats: false }));
        }
    };

    // Загрузка тестов
    const loadTests = async (params = {}) => {
        try {
            setLoading(prev => ({ ...prev, tests: true }));
            const data = await APIGetTests(token, params);
            setTests(data.tests || []);
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке тестов:", error);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, tests: false }));
        }
    };

    // Загрузка обучающих материалов
    const loadMaterials = async (params = {}) => {
        try {
            setLoading(prev => ({ ...prev, materials: true }));
            const data = await APIGetEducationalMaterials(token, params);
            setMaterials(data.materials || []);
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке материалов:", error);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, materials: false }));
        }
    };

    const loadsUnreadCounts = async () => {
        const owner = realtimeAccountRef.current;
        const access = currentRealtimeToken();
        if (!access || !owner) return;
        if (unreadFlightRef.current) { unreadQueuedRef.current = true; return unreadFlightRef.current; }
        const flight = (async () => {
            try {
                const data = await APIGetUnreadCounts(access);
                if (realtimeAccountRef.current === owner)
                    setUnreadCounts({ unread_counts: data.unread_counts || [], timestamp: Date.now() });
            } catch (error) { console.warn('Не удалось обновить непрочитанные сообщения'); }
        })();
        unreadFlightRef.current = flight;
        await flight;
        if (unreadFlightRef.current === flight) unreadFlightRef.current = null;
        if (unreadQueuedRef.current) {
            unreadQueuedRef.current = false;
            return realtimeCallbacksRef.current.loadsUnreadCounts?.();
        }
    };
    realtimeCallbacksRef.current = { loadsUnreadCounts, loadChats };

    // Добавляем функцию загрузки статистики
    const loadDashboardStats = async () => {
        try {
            setStatsLoading(true);
            const data = await APIGetDashboardStats(token);
            if (data.success) {
                setDashboardStats(data);
            }
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке статистики:", error);
            throw error;
        } finally {
            setStatsLoading(false);
        }
    };

    // Загрузка статистики чатов (отдельная от dashboard-stats)
    const loadChatStats = async () => {
        if (!token) return;
        try {
            setChatStatsLoading(true);
            const data = await APIGetChatStats(token);
            if (data.success) {
                setChatStats(data);
            }
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке статистики чатов:", error);
            throw error;
        } finally {
            setChatStatsLoading(false);
        }
    };

    // Загрузка клиентских настроек чата — один раз на старте.
    // Если сервер недоступен — остаются дефолтные значения из state.
    const loadChatSettings = async () => {
        if (!token) return;
        try {
            setChatSettingsLoading(true);
            const data = await APIGetChatSettings(token);
            if (data?.success) {
                setChatSettings({
                    patient_message_max_length: data.patient_message_max_length,
                    message_edit_window_minutes: data.message_edit_window_minutes,
                    chat_max_file_size_mb: data.chat_max_file_size_mb,
                    doctor_response_deadline_hours: data.doctor_response_deadline_hours,
                });
            }
            return data;
        } catch (error) {
            console.error("Ошибка при загрузке настроек чата:", error);
            // Тихо игнорируем — фронт будет работать на дефолтах.
        } finally {
            setChatSettingsLoading(false);
        }
    };

    // Загрузка количества просроченных приёмов.
    // Запрашиваем только для ролей, которые видят пункт "Приёмы" в меню,
    // чтобы не нагружать сервер запросами от пациентов.
    const loadOverdueAppointmentsCount = async () => {
        if (!token) return;
        const allowedRoles = ['admin', 'doctor', 'moderator', 'supervisor'];
        const hasAccess = allowedRoles.some(r => currentDoctor?.type_profile?.includes(r));
        if (!hasAccess) return;

        try {
            const data = await APIGetAppointments(token, { overdue: 'true' });
            setOverdueAppointmentsCount((data?.appointments || []).length);
        } catch (error) {
            console.error("Ошибка при загрузке просроченных приёмов:", error);
            // Тихо игнорируем — бейдж просто не обновится в этот раз.
        }
    };

    // Кол-во новых (необработанных) заявок для бейджа «Заявки». Баг #2.
    const loadNewRequestsCount = async () => {
        if (!token) return;
        const roles = currentDoctor?.type_profile || [];
        const canSee = ['admin', 'doctor', 'duty_doctor', 'moderator', 'supervisor'].some(r => roles.includes(r));
        if (!canSee) return;
        try {
            const data = await APIGetClientRequests(token, { status: 'new', page_size: 1 });
            setNewRequestsCount(data?.new_count ?? data?.count ?? 0);
        } catch (error) {
            // тихо — бейдж просто не обновится в этот раз
        }
    };

    // Периодический опрос просроченных приёмов (30 мин) и новых заявок (1 мин).
    // Первый запрос — сразу после успешной верификации.
    useEffect(() => {
        if (!token || !isVerified || !currentDoctor) return;

        loadOverdueAppointmentsCount();
        loadNewRequestsCount();
        const overdueId = setInterval(loadOverdueAppointmentsCount, 30 * 60 * 1000); // 30 минут
        const requestsId = setInterval(loadNewRequestsCount, 60 * 1000);             // 1 минута

        return () => { clearInterval(overdueId); clearInterval(requestsId); };
    }, [token, isVerified, currentDoctor]);

    // Применить успешный логин (вызывается со страниц логина/регистрации)
    const applyLogin = (profile) => {
        const access = getAccessToken();
        setToken(access);
        setCurrentDoctor(profile);
        setAccount(profile);
        setRegistrationStatus(profile?.registration_status || 'approved');
        const roles = profile?.type_profile || [];
        const isStaff = ['admin', 'doctor', 'duty_doctor', 'moderator', 'supervisor'].some(r => roles.includes(r));
        setIsPatient(!isStaff);
        setIsVerified(true);
        setIsVerifying(false);
        Cookies.set('authToken', access || '');
    };

    // Выход
    const logout = () => {
        // Сначала серверный выход, ПОКА токен ещё валиден — иначе запрос уйдёт с пустым
        // токеном, получит 401 и сработает onAuthFail с неверным редиректом (баг #3).
        // apiLogout сам чистит токены в finally.
        apiLogout().catch(() => {});
        // Затем синхронно сбрасываем токены/состояние — чтобы сброс isVerified и редирект
        // произошли в одном рендере (иначе белый экран AnimatePresence).
        clearTokens();
        Cookies.remove('authToken');
        setToken(null);
        setAccount(null);
        setCurrentDoctor(null);
        setIsVerified(false);
        updateAppBadge(0); // снять бейдж с иконки при выходе
    };
    

    const values = {
        currentDoctor, setCurrentDoctor,
        account, setAccount,
        
        // Состояния
        patients, setPatients,
        chats, setChats,
        tests, setTests,
        materials, setMaterials,
        currentDoctor, setCurrentDoctor,
        isPatient, setIsPatient,

        overdueAppointmentsCount,
        loadOverdueAppointmentsCount,
        loadNewRequestsCount,

        loading,
        token,
        newRequestsCount,
        setNewRequestsCount,
        
        // Функции загрузки
        loadPatients,
        loadChats,
        loadTests,
        loadMaterials,
        loadInitialData,
        loadsUnreadCounts,
        unreadCounts,

        modalUser, setModalUser,
        modalResults, setModalResults,
        modalAssignTest, setModalAssignTest,

        selectedChatToken, setSelectedChatToken,

        dashboardStats,
        statsLoading,
        loadDashboardStats,

        chatStats,
        chatStatsLoading,
        loadChatStats,

        chatSettings,
        chatSettingsLoading,
        loadChatSettings,

        registrationStatus, setRegistrationStatus,
        isApproved: registrationStatus === 'approved',
        applyLogin,
        logout,

        isVerified,
        isVerifying,
    }
    
    return (
        <LayoutContext.Provider value={values}>
            {isVerifying ? <VerificationScreen /> : children}
        </LayoutContext.Provider>
    );
}


// Экран верификации
function VerificationScreen() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: 'white'
        }}>
            <Typography level="h4">Идет верификация...</Typography>
        </div>
    );
}

// Экран неудачной верификации
function VerificationFailedScreen() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: 'white',
            flexDirection: 'column',
            gap: '20px'
        }}>
            <Typography level="h4" color="danger">Ошибка верификации</Typography>
            <Typography>Пожалуйста, убедитесь что вы используете правильную ссылку</Typography>
        </div>
    );
}